import React, { useState, useEffect } from "react";
import {
    Button, Box, TextField, Typography, Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import { useTheme } from "@mui/material";
import { format } from "date-fns"; // Importing date-fns to format the date
import { DatePicker } from "@mui/x-date-pickers/DatePicker"; // Import the DatePicker component
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"; // Adapter for DatePicker
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"; // Localization provider for DatePicker
import RibbonNavbar from "../../components/RibbonNavbar";

const ViewReport = () => {
    const theme = useTheme();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false); // State to manage loading state
    const [selectedDate, setSelectedDate] = useState(null); // Store the selected date
    const [searchQuery, setSearchQuery] = useState(""); // Store search query
    const colors = tokens(theme.palette.mode);
    const [selectedRows, setSelectedRows] = useState([]);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [editMode, setEditMode] = useState(false);

    // Static columns for the grid
    const columns = [
        { field: "slNo", headerName: "Sl.No", flex: 1, minWidth: 90 },
        { field: "date", headerName: "Date", flex: 1, minWidth: 150 },
        { field: "location", headerName: "Location", flex: 1, minWidth: 150 },
        { field: "uploadId", headerName: "Upload ID", flex: 1, minWidth: 150 },
        {
            field: "action",
            headerName: "Action",
            flex: 1,
            minWidth: 150,
            renderCell: (params) => (
                <Button
                    variant="contained"
                    color="error"
                    onClick={() => handleDelete(params.row.id)}
                >
                    Delete
                </Button>
            ),
        },
    ];

    // Sample rows, replace with real data from file
    const sampleRows = [
        {
            id: 1,
            slNo: 1,
            date: "02-03-2025",
            particulars: "Sample Entry 1",
            vchType: "Debit",
            vchNo: "V123",
            debit: "1000.00",
            location: "Room No: 1",
            uploadId: "UP1234",
        },
        {
            id: 2,
            slNo: 2,
            date: "02-03-2025",
            particulars: "Sample Entry 2",
            vchType: "Credit",
            vchNo: "V124",
            debit: "500.00",
            location: "Store Room: 1",
            uploadId: "UP1235",
        },
        {
            id: 3,
            slNo: 3,
            date: "02-03-2025",
            particulars: "Sample Entry 3",
            vchType: "Credit",
            vchNo: "V124",
            debit: "500.00",
            location: "Store Room: 1",
            uploadId: "UP1285",
        },
        {
            id: 4,
            slNo: 4,
            date: "02-03-2025",
            particulars: "Sample Entry 4",
            vchType: "Credit",
            vchNo: "V124",
            debit: "500.00",
            location: "Store Room: 2",
            uploadId: "UP1239",
        },
        // Add more sample rows as needed
    ];

    useEffect(() => {
        // Simulate loading the data
        setRows(sampleRows);
        setLoading(false);
    }, []);

    // Handle the "Delete" button click
    const handleDelete = (id) => {
        setRows((prevRows) => prevRows.filter((row) => row.id !== id)); // Filter out the row with the matching id
    };

    const handleEdit = () => {
        setEditMode(true); // You could toggle or open inline editing if needed
    };

    const handleDeleteFromRibbon = () => {
        setOpenConfirmDialog(true);
    };

    const confirmDelete = () => {
        setRows((prevRows) => prevRows.filter((row) => !selectedRows.includes(row.id)));
        setSelectedRows([]);
        setOpenConfirmDialog(false);
    };

    const cancelDelete = () => {
        setOpenConfirmDialog(false);
    };

    // Handle date change
    const handleDateChange = (newDate) => {
        setSelectedDate(newDate); // Update selected date state
    };

    // Handle search query change
    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value); // Update search query state
    };

    // Handle search button click
    const handleSearchClick = () => {
        // Filter rows based on search query
        const filteredRows = rows.filter(
            (row) =>
                row.particulars.toLowerCase().includes(searchQuery.toLowerCase()) ||
                row.vchNo.toLowerCase().includes(searchQuery.toLowerCase())
        );
        setRows(filteredRows); // Update rows based on the filtered results
    };

    return (
        <div style={{ padding: "10px 20px" }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Header title="Collection Request Report" subtitle="" />

                {/* Date Picker and Search side by side with reduced gap */}
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <DatePicker
                            label="Select Date"
                            value={selectedDate}
                            onChange={handleDateChange}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    sx={{
                                        width: 160,
                                        "& .MuiOutlinedInput-root": {
                                            height: 36,
                                            padding: "0px",
                                        },
                                        "& .MuiOutlinedInput-input": {
                                            padding: "6px 8px",
                                            fontSize: "0.8rem",
                                        },
                                        // When label is not shrunk
                                        "& label.MuiInputLabel-root:not(.MuiInputLabel-shrink) + .MuiInputBase-root .MuiOutlinedInput-input": {
                                            paddingTop: "8px",  // you can adjust this if label overlaps
                                        },
                                        "& .MuiSvgIcon-root": {
                                            fontSize: "18px",
                                        },
                                    }}
                                />
                            )}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSearchClick}
                            sx={{
                                backgroundColor: colors.blueAccent[700],
                                color: colors.primary[400],
                                fontSize: "14px",
                                fontWeight: "bold",
                                minWidth: "100px",
                            }}
                        >
                            Search
                        </Button>
                    </Box>
                </LocalizationProvider>

                {/* Ribbon Navbar */}
                <RibbonNavbar
                    showEdit
                    showDelete
                    onEdit={handleEdit}
                    onDelete={handleDeleteFromRibbon}
                    disabledEdit={selectedRows.length !== 1}
                    disabledDelete={selectedRows.length === 0}
                />
            </Box>
            <Box display="flex" flexDirection="column" gap={1}>
                {/* Date Picker and Search field side by side */}


                {/* Display the selected date above the grid */}
                {selectedDate && (
                    <Typography variant="h6" style={{ marginTop: "10px" }}>
                        Selected Date: {format(selectedDate, "dd-MM-yyyy")}
                    </Typography>
                )}

                {/* Displaying uploaded data in a grid */}
                <div style={{ width: "100%", marginTop: "20px" }}>
                    <Box
                        height="78vh"
                        sx={{
                            "& .MuiDataGrid-root": {
                                border: "none",
                            },
                            "& .MuiDataGrid-cell": {
                                borderBottom: "1px solid grey",
                                whiteSpace: "normal", // Allows text wrapping
                                wordWrap: "break-word", // Ensures text breaks properly
                            },
                            "& .MuiDataGrid-columnHeaders": {
                                backgroundColor: colors.blueAccent[700],
                                borderBottom: "none",
                                fontSize: "14px",
                            },
                            "& .MuiDataGrid-virtualScroller": {
                                backgroundColor: colors.primary[400],
                            },
                            "& .MuiDataGrid-footerContainer": {
                                borderTop: "none",
                                backgroundColor: colors.blueAccent[900],
                                height: "5vh",
                            },
                            "& .MuiCheckbox-root": {
                                color: `${colors.greenAccent[200]} !important`,
                            },
                            "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                                color: `${colors.grey[100]} !important`,
                            },
                        }}
                    >
                        <DataGrid
                            rows={rows}
                            checkboxSelection
                            loading={loading}
                            editMode="row"
                            processRowUpdate={(updatedRow) =>
                                setRows((prevRows) =>
                                    prevRows.map((row) => (row.id === updatedRow.id ? { ...row, ...updatedRow } : row))
                                )
                            }
                            columns={columns.map(col => ({ ...col, editable: col.field !== 'action' }))}
                            components={{ Toolbar: GridToolbar }}
                            onRowSelectionModelChange={(newSelection) => setSelectedRows(newSelection)}
                            sx={{ width: "100%" }}
                        />

                        <Dialog open={openConfirmDialog} onClose={cancelDelete}>
                            <DialogTitle>Confirm Deletion</DialogTitle>
                            <DialogContent>
                                <DialogContentText>
                                    Are you sure you want to delete {selectedRows.length} selected row(s)?
                                </DialogContentText>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={cancelDelete} color="primary">Cancel</Button>
                                <Button onClick={confirmDelete} color="error" variant="contained">Yes, Delete</Button>
                            </DialogActions>
                        </Dialog>
                    </Box>
                </div>
            </Box>
        </div>
    );
};

export default ViewReport;